/**
 * MOI Help Bot — v6.32.100
 *
 * Customer-facing support assistant. Renders as a floating bottom-right pill
 * for signed-in MOSAIC users. Powered by MOSAIC Content Engine via the
 * Emergent content-engine Key, grounded in the Guide / FAQ knowledge base on the
 * backend (HELP_BOT_KNOWLEDGE_BASE in routes/help_bot.py).
 *
 * Renders nothing for signed-out visitors — homepage prospects don't
 * see this widget per operator scoping (signed-in only).
 *
 * Also embedded inline by /pages/Help.js for users who prefer a
 * full-screen reading experience.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bot, MessageCircle, Send, X, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'mosaic_session_token';

const STARTER_PROMPTS = [
  'How do I download a BLM PDF?',
  'What is Content Studio?',
  'How do I create a Teacher Portal account?',
  'How do I assign a digital exam to a student?',
];

const WELCOME_MESSAGE = {
  role: 'assistant',
  text:
    "Hi, I'm MOI — MOSAIC's helper. Ask me about BLMs, the Teacher Portal, " +
    'Content Studio, the digital exam, compliance, or anything else in the platform. ' +
    'For pricing, please email sales@mosaicassessmentco.com or click "Get a Quote" — ' +
    "the sales team handles that directly. I keep answers short and link to the " +
    'Platform Guide for the full step-by-step.',
};

export const MoiHelpBotPanel = ({ embedded = false, onClose, initialOpen = true }) => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    const userTurn = { role: 'user', text: trimmed };
    setMessages((m) => [...m, userTurn]);
    setInput('');
    setSending(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const history = [...messages, userTurn]
        .filter((m) => m !== WELCOME_MESSAGE)
        .slice(-6);
      const res = await axios.post(
        `${API}/api/help-bot/chat`,
        { message: trimmed, history, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: res.data.answer }]);
    } catch (err) {
      const errText =
        err?.response?.data?.detail ||
        "MOI is having trouble right now. Please email support@mosaicassessmentco.com or open the Platform Guide.";
      setMessages((m) => [...m, { role: 'assistant', text: errText, isError: true }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`flex flex-col bg-white border border-gray-200 ${
        embedded ? 'rounded-md shadow-sm h-[640px]' : 'rounded-lg shadow-2xl h-[560px]'
      }`}
      data-testid="moi-helpbot-panel"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white border border-red-200 overflow-hidden flex items-center justify-center">
          <img
            src="/mosaic-mascot.png"
            alt="MOI mascot"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement.innerHTML =
                '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>';
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900">MOI</div>
          <div className="text-xs text-gray-500">MOSAIC's friendly helper</div>
        </div>
        {!embedded && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-white/60 rounded transition-colors"
            aria-label="Close help bot"
            data-testid="moi-helpbot-close"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="moi-helpbot-messages">
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] px-3 py-2 text-sm rounded-lg ${
                m.role === 'user'
                  ? 'bg-red-600 text-white rounded-br-sm'
                  : m.isError
                  ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-bl-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}
              data-testid={`moi-helpbot-msg-${m.role}`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-3 py-2 bg-gray-100 rounded-lg rounded-bl-sm" data-testid="moi-helpbot-typing">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Starter prompts (only show on first turn) */}
      {messages.length <= 1 && !sending && (
        <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="moi-helpbot-starters">
          {STARTER_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => sendMessage(p)}
              className="text-xs px-2.5 py-1 border border-gray-200 hover:border-red-600 hover:text-red-700 rounded-full text-gray-600 transition-colors"
              data-testid="moi-helpbot-starter"
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage(input);
        }}
        className="border-t border-gray-100 p-3 flex gap-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask MOI anything…"
          className="flex-1 px-3 py-2 text-sm border border-gray-200 focus:border-red-600 focus:outline-none rounded transition-colors"
          disabled={sending}
          data-testid="moi-helpbot-input"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="px-3 py-2 bg-red-600 text-white text-sm font-semibold disabled:opacity-40 hover:bg-red-700 transition-colors rounded flex items-center gap-1"
          data-testid="moi-helpbot-send"
        >
          <Send size={14} />
        </button>
      </form>

      {/* Footer */}
      <div className="px-3 pb-2 text-[10px] text-gray-400 flex items-center justify-between">
        <span>Powered by MOSAIC Content Engine</span>
        <a
          href="mailto:support@mosaicassessmentco.com"
          className="hover:text-red-600 inline-flex items-center gap-0.5"
          data-testid="moi-helpbot-escalate"
        >
          Escalate to human <ExternalLink size={9} />
        </a>
      </div>
    </div>
  );
};

const MoiHelpBot = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(true);

  // Hide for signed-out visitors entirely (per operator scoping).
  if (!user) return null;
  // v6.36.16 — Operator requirement: MOI HELP must NEVER appear in
  // student portals at any time. Students see the mascot greeting on
  // login instead; the help bot is for teachers/admins only.
  if (user.role === 'student') return null;

  return (
    <>
      {/* Floating launcher pill */}
      {!open && (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setHasNew(false);
          }}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-xl transition-all hover:scale-105"
          aria-label="Open MOI help"
          data-testid="moi-helpbot-launcher"
        >
          <MessageCircle size={18} />
          <span className="text-sm font-semibold">Ask MOI</span>
          {hasNew && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white" />
          )}
        </button>
      )}

      {/* Floating panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[360px] max-w-[calc(100vw-2rem)]"
          data-testid="moi-helpbot-floating"
        >
          <MoiHelpBotPanel onClose={() => setOpen(false)} />
        </div>
      )}
    </>
  );
};

export default MoiHelpBot;
